import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { WorkspaceProfile } from '../types/decision';
import {
  ShieldCheck,
  Sun,
  Moon,
  Monitor,
  Cloud,
  Printer,
  Building,
  BookOpen,
  Settings,
  HelpCircle,
  Menu,
  X,
  User,
  LogOut,
  ChevronDown,
  Shield,
  KeyRound
} from 'lucide-react';

interface HeaderProps {
  workspace: WorkspaceProfile;
  onOpenSyncModal: () => void;
  onOpenPrintReport: () => void;
  onOpenKnowledgeBase: () => void;
  onOpenSettings: (tab?: string) => void;
  onOpenHelp: () => void;
}

export default function Header({
  workspace,
  onOpenSyncModal,
  onOpenPrintReport,
  onOpenKnowledgeBase,
  onOpenSettings,
  onOpenHelp
}: HeaderProps) {
  const { theme, setTheme } = useTheme();
  const { user, logout, isAuthenticated, authDisabled } = useAuth();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  // Online / Offline listeners
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Smart cycle theme: light -> dark -> system -> light
  const handleCycleTheme = () => {
    if (theme === 'light') setTheme('dark');
    else if (theme === 'dark') setTheme('system');
    else setTheme('light');
  };

  // Helper for user initials
  const getUserInitials = (name?: string, username?: string) => {
    if (name) {
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      return name.slice(0, 2).toUpperCase();
    }
    if (username) return username.slice(0, 2).toUpperCase();
    return 'AU';
  };

  return (
    <header className="sticky top-0 z-30 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-12 gap-3 sm:gap-4">
          
          {/* ========================================================= */}
          {/* 1. LEFT: Brand & Entity Context Badge */}
          {/* ========================================================= */}
          <div className="flex items-center gap-3 shrink-0">
            
            {/* Logo */}
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-accent-dark to-accent flex items-center justify-center text-white shadow-xs shadow-accent/20">
                <ShieldCheck className="w-4.5 h-4.5" />
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-1.5">
                  <span className="font-black text-sm sm:text-base tracking-tight text-slate-900 dark:text-slate-100">
                    DPDP Obligations
                  </span>
                  <div
                    className="flex items-center"
                    title={isOnline ? 'Online • Keycloak SSO Connected' : 'Offline Mode • Local IDB Persistent'}
                  >
                    <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
                  </div>
                </div>
              </div>
            </div>

            {/* Entity Badge Button (Opens Full Settings on Legal Entity tab) */}
            <button
              onClick={() => onOpenSettings('entity')}
              className="h-8 px-3 rounded-xl text-xs font-medium bg-slate-50 hover:bg-slate-100 dark:bg-slate-800/60 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700/80 text-slate-700 dark:text-slate-300 transition-all hover:border-accent group cursor-pointer inline-flex items-center gap-2"
              title="Click to view & edit Legal Entity Profile & Regulatory Scope in Settings"
            >
              <Building className="w-3.5 h-3.5 text-slate-400 group-hover:text-accent shrink-0 transition-colors" />
              <span className="max-w-[120px] sm:max-w-[180px] truncate font-semibold">
                {workspace.entity_name}
              </span>

              {workspace.is_sdf && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-extrabold bg-purple-500/15 text-purple-700 dark:text-purple-300 border border-purple-500/30 shrink-0">
                  SDF
                </span>
              )}
            </button>

          </div>

          {/* ========================================================= */}
          {/* 2. CENTER: Clean Minimalist Nav Items (Desktop) */}
          {/* ========================================================= */}
          <nav className="hidden md:flex items-center">
            <button
              onClick={onOpenKnowledgeBase}
              className="h-8 px-3 rounded-xl text-xs font-semibold text-slate-700 dark:text-slate-300 bg-slate-100 hover:bg-slate-200/80 dark:bg-slate-800 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 hover:border-accent dark:hover:border-accent transition-all cursor-pointer inline-flex items-center gap-1.5"
              title="In-App Statutory & Architecture Knowledge Base"
            >
              <BookOpen className="w-3.5 h-3.5 text-accent" />
              <span>Knowledge Base</span>
            </button>
          </nav>

          {/* ========================================================= */}
          {/* 3. RIGHT: Actions & Utilities */}
          {/* ========================================================= */}
          <div className="flex items-center gap-2">
            
            {/* Sync & BYOD (Primary Action Button) */}
            <button
              onClick={onOpenSyncModal}
              className="h-8 px-3 rounded-xl text-xs font-bold text-white bg-accent hover:bg-accent-dark transition-all shadow-xs shadow-accent/20 cursor-pointer inline-flex items-center gap-1.5"
              title="BYOD Backend Sync & Snapshot Export/Import"
            >
              <Cloud className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Sync & BYOD</span>
              <span className="sm:hidden">Sync</span>
            </button>

            {/* Audit Report Button (Desktop) */}
            <button
              onClick={onOpenPrintReport}
              className="hidden lg:inline-flex items-center gap-1.5 h-8 px-3 rounded-xl text-xs font-semibold text-slate-700 dark:text-slate-300 bg-slate-100 hover:bg-slate-200/80 dark:bg-slate-800 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 transition-colors cursor-pointer"
              title="Printable Compliance Workpaper Dossier"
            >
              <Printer className="w-3.5 h-3.5 text-slate-500" />
              <span>Audit Report</span>
            </button>

            <div className="hidden sm:block h-4 w-[1px] bg-slate-200 dark:bg-slate-700/80 mx-0.5" />

            {/* Master Data Settings Button */}
            <button
              onClick={() => onOpenSettings()}
              className="h-8 w-8 rounded-xl inline-flex items-center justify-center text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 border border-transparent hover:border-slate-200 dark:border-slate-700 transition-colors cursor-pointer"
              title="Master Data & Taxonomy Settings"
            >
              <Settings className="w-4 h-4" />
            </button>

            {/* Help & User Guide Button */}
            <button
              onClick={onOpenHelp}
              className="h-8 w-8 rounded-xl inline-flex items-center justify-center text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 border border-transparent hover:border-slate-200 dark:border-slate-700 transition-colors cursor-pointer"
              title="Application User Guide & Step-by-Step Help (?)"
            >
              <HelpCircle className="w-4 h-4 text-accent" />
            </button>

            {/* Theme Toggle (Single Smart Cycle Button) */}
            <button
              onClick={handleCycleTheme}
              className="h-8 w-8 rounded-xl inline-flex items-center justify-center text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 border border-transparent hover:border-slate-200 dark:border-slate-700 transition-colors cursor-pointer"
              title={`Theme: ${theme.toUpperCase()} (Click to toggle)`}
            >
              {theme === 'light' && <Sun className="w-4 h-4 text-amber-500" />}
              {theme === 'dark' && <Moon className="w-4 h-4 text-sky-400" />}
              {theme === 'system' && <Monitor className="w-4 h-4 text-accent" />}
            </button>

            {/* User Profile Badge & SSO Logout Dropdown */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="h-8 pl-1.5 pr-2.5 rounded-xl inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200/80 dark:bg-slate-800 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 hover:border-accent dark:hover:border-accent transition-all cursor-pointer text-slate-700 dark:text-slate-200 group"
                title="Keycloak Authenticated User Session"
              >
                <div className="w-5.5 h-5.5 rounded-lg bg-accent/20 border border-accent/40 text-accent font-bold text-[10px] flex items-center justify-center shrink-0">
                  {getUserInitials(user?.name, user?.username)}
                </div>
                <span className="hidden sm:inline text-xs font-semibold max-w-[100px] truncate">
                  {user?.name || user?.username || 'User'}
                </span>
                <ChevronDown className="w-3 h-3 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-200 transition-transform duration-150" />
              </button>

              {/* Profile Dropdown Popover */}
              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl p-3 z-50 animate-in fade-in slide-in-from-top-2 duration-150 space-y-3">
                  {/* User details header */}
                  <div className="flex items-center gap-3 p-2 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-800">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-accent-dark to-accent flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-xs shadow-accent/20">
                      {getUserInitials(user?.name, user?.username)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100 truncate">
                        {user?.name || user?.username || 'Authorized Officer'}
                      </h4>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                        {user?.email || 'authenticated-session'}
                      </p>
                    </div>
                  </div>

                  {/* Auth Realm Info */}
                  <div className="px-2 py-1.5 text-[11px] rounded-lg bg-slate-100/70 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1 text-slate-500 font-medium">
                        <KeyRound className="w-3 h-3 text-accent" />
                        Auth Realm:
                      </span>
                      <span className="font-mono text-[10px] font-bold text-slate-800 dark:text-slate-200">
                        {authDisabled ? 'offline-mode' : 'dpdp-dhristhi'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1 text-slate-500 font-medium">
                        <Shield className="w-3 h-3 text-emerald-500" />
                        Status:
                      </span>
                      <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                        {authDisabled ? 'Bypassed (Dev)' : 'Active SSO Session'}
                      </span>
                    </div>
                  </div>

                  {/* Roles Pill List */}
                  {user?.roles && user.roles.length > 0 && (
                    <div className="px-1 space-y-1">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                        Assigned Permissions:
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {user.roles.map((role) => (
                          <span
                            key={role}
                            className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700"
                          >
                            {role}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="h-[1px] bg-slate-100 dark:bg-slate-800" />

                  {/* Logout Button */}
                  <button
                    onClick={() => {
                      setIsProfileOpen(false);
                      logout();
                    }}
                    className="w-full py-2 px-3 rounded-xl text-xs font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 border border-transparent hover:border-red-200 dark:hover:border-red-900/50 transition-all flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    <span>Sign Out of Dhristhi SSO</span>
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Hamburger Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden h-8 w-8 rounded-xl inline-flex items-center justify-center text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
              title="Open Navigation Menu"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

          </div>

        </div>
      </div>

      {/* ========================================================= */}
      {/* 4. MOBILE SLIDE-DOWN DRAWER MENU */}
      {/* ========================================================= */}
      {isMobileMenuOpen && (
        <div className="md:hidden border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3 space-y-2 animate-in slide-in-from-top-2 duration-150">
          
          {/* User info in mobile menu */}
          <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-accent/20 border border-accent/40 text-accent font-bold text-xs flex items-center justify-center shrink-0">
                {getUserInitials(user?.name, user?.username)}
              </div>
              <div>
                <div className="text-xs font-bold text-slate-800 dark:text-slate-200">
                  {user?.name || user?.username || 'Authorized User'}
                </div>
                <div className="text-[10px] text-slate-500">
                  {user?.email || 'Keycloak Session'}
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                logout();
              }}
              className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg text-xs"
              title="Sign Out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center justify-between py-1 text-xs text-slate-500 border-b border-slate-100 dark:border-slate-800">
            <span>Workspace Profile</span>
            <span className="font-semibold text-slate-800 dark:text-slate-200">{workspace.entity_name}</span>
          </div>

          <div className="grid grid-cols-2 gap-2 pt-1">
            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                onOpenKnowledgeBase();
              }}
              className="flex items-center gap-2 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-left text-xs font-semibold text-slate-800 dark:text-slate-200 cursor-pointer"
            >
              <BookOpen className="w-4 h-4 text-accent" />
              <span>Knowledge Base</span>
            </button>

            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                onOpenPrintReport();
              }}
              className="flex items-center gap-2 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-left text-xs font-semibold text-slate-800 dark:text-slate-200 cursor-pointer"
            >
              <Printer className="w-4 h-4 text-slate-500" />
              <span>Audit Report</span>
            </button>

            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                onOpenSettings('entity');
              }}
              className="flex items-center gap-2 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-left text-xs font-semibold text-slate-800 dark:text-slate-200 cursor-pointer"
            >
              <Building className="w-4 h-4 text-slate-600 dark:text-slate-400" />
              <span>Entity Profile</span>
            </button>

            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                onOpenSettings();
              }}
              className="flex items-center gap-2 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-left text-xs font-semibold text-slate-800 dark:text-slate-200 cursor-pointer"
            >
              <Settings className="w-4 h-4 text-slate-600 dark:text-slate-400" />
              <span>Settings</span>
            </button>

            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                onOpenHelp();
              }}
              className="col-span-2 flex items-center gap-2 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-left text-xs font-semibold text-slate-800 dark:text-slate-200 cursor-pointer"
            >
              <HelpCircle className="w-4 h-4 text-accent" />
              <span>User Guide & Step-by-Step Help</span>
            </button>
          </div>

          <div className="pt-2 flex items-center justify-between text-xs text-slate-500 border-t border-slate-100 dark:border-slate-800">
            <span className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-500' : 'bg-amber-500'}`} />
              <span>{isOnline ? 'IndexedDB Sync Ready' : 'Offline Mode'}</span>
            </span>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setTheme('light')}
                className={`p-1.5 rounded-lg ${theme === 'light' ? 'bg-slate-200 dark:bg-slate-700 text-amber-600' : 'text-slate-400'}`}
                title="Light"
              >
                <Sun className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setTheme('dark')}
                className={`p-1.5 rounded-lg ${theme === 'dark' ? 'bg-slate-200 dark:bg-slate-700 text-sky-400' : 'text-slate-400'}`}
                title="Dark"
              >
                <Moon className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setTheme('system')}
                className={`p-1.5 rounded-lg ${theme === 'system' ? 'bg-slate-200 dark:bg-slate-700 text-accent' : 'text-slate-400'}`}
                title="System"
              >
                <Monitor className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

        </div>
      )}
    </header>
  );
}
